import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Stepper } from '@/components/ui/Stepper';
import { useToast } from '@/components/ui/Toast';
import { roundsApi } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/queries';
import type { CourseDetail, RecordHoleScoreInput, Round } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { EM_DASH, fmtToPar } from '@/lib/format/number';
import { fmtDistance, loadUnit } from '@/lib/geo/units';
import { t } from '@/lib/i18n';
import { ScoreGrid, type GridHole } from './ScoreGrid';
import styles from './Record.module.css';
import { MinusIcon } from '@/components/icons';

/** Matches the 900px breakpoint the shell uses to switch between mobile and desktop chrome. */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

/** What the player has entered for one hole, before it reaches the server. */
interface Draft {
  strokes: number;
  putts: number;
  fairwayHit: boolean | null;
  penaltyStrokes: number;
  bunkerShots: number;
}

type SaveState = 'idle' | 'pending' | 'saved' | 'error';

const SAVE_DEBOUNCE_MS = 700;

interface HoleEntryProps {
  round: Round;
  course: CourseDetail | undefined;
  header: (right: ReactNode) => ReactNode;
  onReview: () => void;
}

export function HoleEntry({ round, course, header, onReview }: HoleEntryProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const unit = loadUnit();

  const physicalCount = course?.totalHoles ?? round.holesPlayed;

  /** Par and reference data for every logical hole, including the repeat on a double round. */
  const holeInfo = useMemo(
    () =>
      Array.from({ length: round.holesPlayed }, (_, i) => {
        const number = i + 1;
        const physical = ((number - 1) % physicalCount) + 1;
        const ch = course?.holes.find((h) => h.number === physical);
        const saved = round.scores.find((s) => s.holeNumber === number);
        // On a double round the back nine uses the second tee.
        const teeName =
          number > physicalCount && round.teeBoxNameBack ? round.teeBoxNameBack : round.teeBoxName;
        return {
          number,
          physical,
          par: saved?.par ?? ch?.par ?? 4,
          strokeIndex: ch?.handicapIndex ?? null,
          distanceMeters: ch?.distancesByTee[teeName] ?? null,
        };
      }),
    [round, course, physicalCount],
  );

  const [current, setCurrent] = useState(() => firstUnfilled(round));
  const [saveState, setSaveState] = useState<SaveState>('idle');
  // At a desk the whole card should be visible; at the tee, one hole at a time.
  const isDesktop = useIsDesktop();
  const [mode, setMode] = useState<'hole' | 'grid' | null>(null);
  const effectiveMode = mode ?? (isDesktop ? 'grid' : 'hole');

  /** Local edits, keyed by hole. Seeded from the server and kept in step with it. */
  const [drafts, setDrafts] = useState<Map<number, Draft>>(() => draftsFrom(round));
  useEffect(() => setDrafts((d) => mergeServerScores(d, round)), [round]);

  const timers = useRef(new Map<number, number>());

  const persist = useCallback(
    async (holeNumber: number, draft: Draft, par: number) => {
      setSaveState('pending');
      try {
        const body: RecordHoleScoreInput = {
          strokes: draft.strokes,
          putts: draft.putts,
          penaltyStrokes: draft.penaltyStrokes,
          bunkerShots: draft.bunkerShots,
          // The server forces fairwayHit to null on a par 3; sending it is pointless and
          // sending an unknown value would be a lie, so it is omitted in both cases.
          ...(par !== 3 && draft.fairwayHit !== null ? { fairwayHit: draft.fairwayHit } : {}),
        };
        await roundsApi.recordHole(round.id, holeNumber, body);
        setSaveState('saved');
        void queryClient.invalidateQueries({ queryKey: qk.round(round.id) });
      } catch (e) {
        setSaveState('error');
        toast.error(t('record.saveHoleFailed', { number: holeNumber }), {
          label: t('record.retry'),
          onClick: () => void persist(holeNumber, draft, par),
        });
        if (e instanceof Error) console.error(e);
      }
    },
    [round.id, queryClient, toast],
  );

  /** Debounced per hole, so tapping a stepper five times is one request, not five. */
  const scheduleSave = useCallback(
    (holeNumber: number, draft: Draft, par: number) => {
      const existing = timers.current.get(holeNumber);
      if (existing) window.clearTimeout(existing);
      setSaveState('pending');
      const id = window.setTimeout(() => {
        timers.current.delete(holeNumber);
        void persist(holeNumber, draft, par);
      }, SAVE_DEBOUNCE_MS);
      timers.current.set(holeNumber, id);
    },
    [persist],
  );

  /** Anything still queued must go out before the player leaves the step. */
  const flush = useCallback(() => {
    for (const [holeNumber, timerId] of timers.current) {
      window.clearTimeout(timerId);
      const draft = drafts.get(holeNumber);
      const info = holeInfo.find((h) => h.number === holeNumber);
      if (draft && info) void persist(holeNumber, draft, info.par);
    }
    timers.current.clear();
  }, [drafts, holeInfo, persist]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const id of pending.values()) window.clearTimeout(id);
    };
  }, []);

  const info = holeInfo[current - 1];
  const draft = info ? (drafts.get(current) ?? blankDraft(info.par)) : null;

  const update = (patch: Partial<Draft>) => {
    if (!info || !draft) return;
    const next = { ...draft, ...patch };
    // Putts can never exceed strokes: you cannot two-putt a hole-in-one.
    if (next.putts > next.strokes) next.putts = next.strokes;
    setDrafts((d) => new Map(d).set(current, next));
    scheduleSave(current, next, info.par);
  };

  const filledCount = drafts.size;
  const runningStrokes = sumDrafts(drafts, (d) => d.strokes);
  const runningPar = [...drafts.keys()].reduce((sum, n) => sum + (holeInfo[n - 1]?.par ?? 0), 0);

  const goTo = (n: number) => {
    flush();
    setCurrent(Math.min(round.holesPlayed, Math.max(1, n)));
  };

  const savedIndicator = (
    <span className={styles.savedTag}>
      <span
        className={cx(styles.savedDot, saveState === 'pending' && styles.savedDotPending)}
        aria-hidden="true"
      />
      {saveState === 'pending'
        ? t('record.saving')
        : saveState === 'error'
          ? t('record.saveError')
          : t('record.saved')}
    </span>
  );

  if (!info || !draft) return null;

  if (effectiveMode === 'grid') {
    const gridHoles: GridHole[] = holeInfo.map((h) => {
      const d = drafts.get(h.number);
      return {
        number: h.number,
        par: h.par,
        strokes: d?.strokes ?? null,
        putts: d?.putts ?? null,
        fairwayHit: d?.fairwayHit ?? null,
        // Derived locally with the same rule the server applies, so the read-only column
        // updates as you type instead of waiting for a round-trip.
        greenInRegulation: d ? girOf(d, h.par) : null,
      };
    });

    return (
      <>
        {header(savedIndicator)}
        <ScoreGrid
          holes={gridHoles}
          filledCount={filledCount}
          onChange={(holeNumber, patch) => {
            const hole = holeInfo[holeNumber - 1];
            if (!hole) return;
            const base = drafts.get(holeNumber) ?? blankDraft(hole.par);
            const next = { ...base, ...patch };
            if (next.putts > next.strokes) next.putts = next.strokes;
            setDrafts((d) => new Map(d).set(holeNumber, next));
          }}
          onCommit={(holeNumber) => {
            const hole = holeInfo[holeNumber - 1];
            const d = drafts.get(holeNumber);
            if (hole && d) void persist(holeNumber, d, hole.par);
          }}
          onExit={() => setMode('hole')}
          onReview={() => {
            flush();
            onReview();
          }}
        />
      </>
    );
  }

  return (
    <>
      {header(savedIndicator)}

      <div className={styles.header} style={{ paddingTop: 0 }}>
        <div className={styles.holeHead}>
          <div>
            <div className={styles.holeNumber}>{t('hole.number', { number: info.number })}</div>
            <div className={styles.holeFacts}>
              <span className={styles.holePar}>{t('course.par', { value: info.par })}</span>
              {info.strokeIndex != null && (
                <span className={styles.holeSi}>{t('record.si', { value: info.strokeIndex })}</span>
              )}
              {info.distanceMeters != null && (
                <span className={styles.holeDistance}>
                  {fmtDistance(info.distanceMeters, unit)}
                </span>
              )}
            </div>
          </div>
          <div className={styles.runningTotal}>
            <div className={styles.runningLabel}>{t('record.total')}</div>
            <div className={styles.runningValue}>
              {runningStrokes || EM_DASH}{' '}
              <span className={styles.runningToPar}>
                {filledCount > 0 ? fmtToPar(runningStrokes - runningPar) : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.strokesBlock}>
          <div className={styles.secondaryLabel}>{t('round.strokes')}</div>
          <div style={{ marginTop: '0.625rem' }}>
            <Stepper
              label={t('record.strokesLower')}
              size="lg"
              value={draft.strokes}
              min={1}
              max={20}
              onChange={(v) => update({ strokes: v })}
              hint={t('record.strokesHint', { toPar: fmtToPar(draft.strokes - info.par) })}
            />
          </div>
        </div>

        <div className={styles.secondaryRow}>
          <div className={styles.secondaryCard}>
            <div className={styles.secondaryLabel}>{t('round.putts')}</div>
            <div style={{ marginTop: '0.4375rem' }}>
              <Stepper
                label={t('record.puttsLower')}
                size="sm"
                value={draft.putts}
                min={0}
                max={draft.strokes}
                onChange={(v) => update({ putts: v })}
              />
            </div>
          </div>

          <div className={styles.secondaryCard}>
            <div className={styles.secondaryLabel}>{t('round.fairway')}</div>
            {info.par === 3 ? (
              <p className={styles.derivedNote} style={{ marginTop: '0.6875rem' }}>
                {t('record.par3NoFairway')}
              </p>
            ) : (
              <div className={styles.toggleRow}>
                <Pill
                  selected={draft.fairwayHit === false}
                  onClick={() => update({ fairwayHit: false })}
                >
                  {t('record.missed')}
                </Pill>
                <Pill
                  selected={draft.fairwayHit === true}
                  onClick={() => update({ fairwayHit: true })}
                >
                  {t('record.hit')}
                </Pill>
              </div>
            )}
          </div>
        </div>

        <div className={styles.counterRow}>
          <Counter
            label={t('round.penalty')}
            value={draft.penaltyStrokes}
            onChange={(v) => update({ penaltyStrokes: v })}
          />
          <Counter
            label={t('round.bunker')}
            value={draft.bunkerShots}
            onChange={(v) => update({ bunkerShots: v })}
          />
        </div>

        <div className={styles.derived}>
          <span>
            <strong>{t('round.gir')}</strong>{' '}
            <span style={{ color: 'var(--text-secondary)' }}>{t('record.automatic')}</span>
          </span>
          <span style={{ color: 'var(--text-secondary)' }}>
            {girOf(draft, info.par) ? t('record.yes') : t('record.no')}
          </span>
        </div>

        <div className={styles.strip}>
          <div className={styles.stripRule} aria-hidden="true" />
          <div className={cx(styles.stripScroll, 'scroll-x')}>
            {holeInfo.map((h) => (
              <button
                key={h.number}
                type="button"
                className={cx(
                  styles.stripItem,
                  drafts.has(h.number) && styles.stripItemFilled,
                  h.number === current && styles.stripItemCurrent,
                )}
                onClick={() => goTo(h.number)}
                aria-label={
                  drafts.has(h.number)
                    ? t('record.holeFilled', { number: h.number })
                    : t('hole.number', { number: h.number })
                }
                aria-current={h.number === current ? 'true' : undefined}
              >
                {h.number}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.navRow}>
          <Button
            variant="secondary"
            size="lg"
            disabled={current === 1}
            onClick={() => goTo(current - 1)}
          >
            {t('record.previous')}
          </Button>
          {current < round.holesPlayed ? (
            <Button size="lg" onClick={() => goTo(current + 1)}>
              {t('hole.number', { number: current + 1 })}
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={() => {
                flush();
                onReview();
              }}
            >
              {t('record.review')}
            </Button>
          )}
        </div>

        <div className={styles.saveAndReview}>
          {isDesktop && (
            <Button variant="secondary" size="sm" onClick={() => setMode('grid')}>
              {t('record.seeGrid')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              flush();
              onReview();
            }}
          >
            {t('record.saveAndReview', { filled: filledCount, total: round.holesPlayed })}
          </Button>
        </div>
      </div>
    </>
  );
}

function Counter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className={styles.counterCard}>
      <span className={styles.counterLabel}>{label}</span>
      <span className={styles.counterButtons}>
        <button
          type="button"
          className={styles.counterBtn}
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={value === 0}
          aria-label={t('a11y.decrease', { label })}
        >
          <MinusIcon size={18} />
        </button>
        <span className={styles.counterValue}>{value}</span>
        <button
          type="button"
          className={styles.counterBtn}
          onClick={() => onChange(value + 1)}
          aria-label={t('a11y.increase', { label })}
        >
          +
        </button>
      </span>
    </div>
  );
}

// ------------------------------------------------------------- helpers

function blankDraft(par: number): Draft {
  // Starting at par is the canvas's choice: most holes are played at or near it, so the
  // stepper usually needs one or two taps rather than four from zero.
  return { strokes: par, putts: 2, fairwayHit: null, penaltyStrokes: 0, bunkerShots: 0 };
}

function draftsFrom(round: Round): Map<number, Draft> {
  const map = new Map<number, Draft>();
  for (const s of round.scores) {
    map.set(s.holeNumber, {
      strokes: s.strokes,
      putts: s.putts ?? 0,
      fairwayHit: s.fairwayHit,
      penaltyStrokes: s.penaltyStrokes,
      bunkerShots: s.bunkerShots,
    });
  }
  return map;
}

/** Adopts server scores for holes the client has not touched, without clobbering edits. */
function mergeServerScores(current: Map<number, Draft>, round: Round): Map<number, Draft> {
  const next = new Map(current);
  for (const s of round.scores) {
    if (!next.has(s.holeNumber)) {
      next.set(s.holeNumber, {
        strokes: s.strokes,
        putts: s.putts ?? 0,
        fairwayHit: s.fairwayHit,
        penaltyStrokes: s.penaltyStrokes,
        bunkerShots: s.bunkerShots,
      });
    }
  }
  return next;
}

function firstUnfilled(round: Round): number {
  const filled = new Set(round.scores.map((s) => s.holeNumber));
  for (let n = 1; n <= round.holesPlayed; n++) if (!filled.has(n)) return n;
  return 1;
}

function sumDrafts(drafts: Map<number, Draft>, pick: (d: Draft) => number): number {
  let total = 0;
  for (const d of drafts.values()) total += pick(d);
  return total;
}

/** Mirrors the server's derivation so the read-only row is not a guess. */
function girOf(draft: Draft, par: number): boolean {
  return draft.strokes - draft.putts <= par - 2;
}
